import * as _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  HelpBlock,
  ListGroup,
  ListGroupItem,
  Modal,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { FC, MouseEvent } from 'react';

import { DISABLE_ORG_BOM_V2, TEAM_GROUP_PACKAGE_REPORT } from '../../../shared/featureFlags/flagNames';
import { useAddToast } from '../../contexts/ToastContext';
import { window } from '../../globals';
import { useIsFeatureEnabled } from '../../helpers/features/featureFlags';
import { useTeamsReleaseGroups } from '../../hooks/api/teams/useTeamReleaseGroups';
import useIsLicenseConcludedEnabled from '../../hooks/features/useIsLicenseConcludedEnabled';
import { getErrorMessage } from '../../ReactQuery/errors';
import { get as apiGet, post } from '../../redux/api';
import TeamsModule from '../../redux/teams';
import IconButton from '../generic/Button/IconButton';
import Flex from '../generic/Flex';
import LabeledCheckbox from '../generic/Forms/LabeledCheckbox';
import Icon, { icons, IconText } from '../generic/Icon';
import Loader from '../generic/Loader';
import { useAddJobToNotificationQueue } from '../JobNotificationHub/Provider';
import type { Task } from '../../../shared/model-typings';
import type { SelectOption, TeamSelection } from '../../types';
import type { AddJobFn } from '../JobNotificationHub/types';

import { BulkReportScopeField } from './BulkReportScopeField';
import modalCss from './ModalBulkReport.module.less';

type AttributionFileMetadata = {
  file: string;
  project: string;
  time: string | number;
  timeObj?: moment.Moment;
  type: 'PROJECT' | 'NOTICE'
};

type State = {
  loading: boolean;
  reports: AttributionFileMetadata[];
  error: Error | null;
  config: Record<string, string[]>;
  isCreatingReport: boolean;
  isSubmittingReportJob: boolean;
  reportTask: Task | null;
  includeBinaryScans: boolean;
  // Remembers the breadcrumb selection the release group was picked under, so it drops out once that changes.
  releaseGroup: { id: number; selectionKey: string } | null;
};

type Props = {
  show?: boolean;
  showBinaryScansOption?: boolean;
  onHide: (e?: MouseEvent<HTMLElement>) => void;
  selectedTeams?: TeamSelection;
  onAddJobToNotificationQueue: AddJobFn;
};

type CheckFieldProps = {
  label: string;
  field?: string;
  disabled?: boolean;
  checked: boolean;
  onChange: (value: string) => void;
};

type BinaryScanCheckFieldProps = {
  disabled?: boolean;
  checked: boolean;
  onChange: () => void;
};

type ContentPanelProps = {
  loading: boolean;
  reports: AttributionFileMetadata[];
  isCreatingReport: boolean;
  reportTask: Task | null;
  isSubmittingReportJob: boolean;
  config: Record<string, string[]>;
  includeBinaryScans: boolean;
  showBinaryScansOption?: boolean;
  // Non-empty only when the breadcrumb is scoped to teams / team groups and the drill-down is enabled.
  scopedTeamIds: number[];
  releaseGroupOptions: Array<SelectOption<number>>;
  isLoadingReleaseGroups: boolean;
  releaseGroupId: number | null;
  onReleaseGroupChange: (releaseGroupId: number | null) => void;
  defaultEmail: string;
  onLoad: () => void;
  onSubmitReport: (filename: string | null, email: string) => void;
  handleCheckFieldChange: (checkKey: string) => void;
  handleBinaryScanChange: () => void;
  onHide?: (e?: MouseEvent<HTMLElement>) => void;
  setIsCreatingReport: (value: boolean) => void;
};

/**
 * CheckField component for rendering checkbox fields with labels
 */
const CheckField: FC<CheckFieldProps> = ({ label, field, disabled = false, checked, onChange }) => {
  const checkKey = (field || label).toUpperCase();

  return (
    <LabeledCheckbox
      label={label}
      isChecked={checked}
      isDisabled={disabled}
      onChange={() => onChange(checkKey)}
    />
  );
};

/**
 * BinaryScanCheckField component for rendering binary scan checkbox field
 */
const BinaryScanCheckField: FC<BinaryScanCheckFieldProps> = ({ disabled = false, checked, onChange }) => {
  return (
    <LabeledCheckbox
      label="Packages from Binary Decomposition"
      isChecked={checked}
      isDisabled={disabled}
      onChange={onChange}
    />
  );
};

/**
 * ContentPanel component for rendering different content based on state
 */
const ContentPanel: FC<ContentPanelProps> = ({
  loading,
  reports,
  isCreatingReport,
  reportTask,
  isSubmittingReportJob,
  config,
  includeBinaryScans,
  showBinaryScansOption,
  scopedTeamIds,
  releaseGroupOptions,
  isLoadingReleaseGroups,
  releaseGroupId,
  onReleaseGroupChange,
  defaultEmail,
  onLoad,
  onSubmitReport,
  handleCheckFieldChange,
  handleBinaryScanChange,
  onHide,
  setIsCreatingReport,
}) => {
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const organizationId = window.user?.organization?.id;
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId as number);

  if (loading) {
    return (
      <div className="text-center">
        <Loader />
      </div>
    );
  }

  if (isCreatingReport) {
    return (
      reportTask ? (
        <div className="text-center">
          <img src="/img/success-modal-image.svg" alt="Success" width="100%" />
          <h4>Report Job Submitted</h4>
          <p>
            FOSSA will now proceed to collect data from across your organization<br />and send you an email when
            finished.
          </p>
          <Button onClick={() => onHide && onHide()}>Finish</Button>
        </div>
      ) : (
        <div>
          <h3>Configure Package Report</h3>
          <br />
          <Form horizontal>
            <FormGroup>
              <Col componentClass={ControlLabel} sm={2}>
                Format
              </Col>
              <Col sm={10}>
                <FormControl componentClass="select" placeholder="CSV" disabled>
                  <option value="CSV">CSV</option>
                </FormControl>
              </Col>
            </FormGroup>
            {scopedTeamIds.length > 0 && (
              <BulkReportScopeField
                selectedTeamIds={scopedTeamIds}
                releaseGroupOptions={releaseGroupOptions}
                isLoadingReleaseGroups={isLoadingReleaseGroups}
                releaseGroupId={releaseGroupId}
                onReleaseGroupChange={onReleaseGroupChange}
                isDisabled={isSubmittingReportJob}
              />
            )}
            <FormGroup>
              <Col componentClass={ControlLabel} sm={2}>
                Project Data
              </Col>
              <Col sm={5}>
                <div className={modalCss.checkboxes}>
                  <CheckField
                    label="Package"
                    checked={!!config?.columns?.includes('PACKAGE')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  <CheckField
                    label="Authors"
                    checked={!!config?.columns?.includes('AUTHORS')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  <CheckField
                    label="Description"
                    checked={!!config?.columns?.includes('DESCRIPTION')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  <CheckField
                    label="Teams"
                    field="TEAMS"
                    checked={!!config?.columns?.includes('TEAMS')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  {showBinaryScansOption && (
                    <BinaryScanCheckField
                      checked={includeBinaryScans}
                      disabled={isSubmittingReportJob}
                      onChange={handleBinaryScanChange}
                    />
                  )}
                </div>
              </Col>
              <Col sm={5}>
                <div className={modalCss.checkboxes}>
                  <CheckField
                    label="Package Homepage"
                    field="PROJECTURL"
                    checked={!!config?.columns?.includes('PROJECTURL')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  <CheckField
                    label="Package Manager"
                    field="SOURCE"
                    checked={!!config?.columns?.includes('SOURCE')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  <CheckField
                    label="Dependency Paths"
                    field="DEPENDENCYPATHS"
                    checked={!!config?.columns?.includes('DEPENDENCYPATHS')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  <CheckField
                    label="Projects"
                    field="PROJECTS"
                    checked={!!config?.columns?.includes('PROJECTS')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                  <CheckField
                    label="Issue Resolution Notes"
                    field="ISSUENOTES"
                    checked={!!config?.columns?.includes('ISSUENOTES')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                </div>
              </Col>
            </FormGroup>

            <FormGroup>
              <Col componentClass={ControlLabel} sm={2}>
                License Detail
              </Col>
              <Col sm={5}>
                <div className={modalCss.checkboxes}>
                  <CheckField
                    label="License Template"
                    field="LICENSE"
                    checked={!!config?.columns?.includes('LICENSE')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                </div>
              </Col>
              <Col sm={5}>
                <div className={modalCss.checkboxes}>
                  <CheckField
                    label="Discovered License(s)"
                    field="OTHERLICENSES"
                    checked={!!config?.columns?.includes('OTHERLICENSES')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                  />
                </div>
              </Col>
              {isLicenseConcludedEnabled && (
                <Col sm={5}>
                  <CheckField
                    label="Concluded License"
                    field="CONCLUDEDLICENSE"
                    checked={!!config?.columns?.includes('CONCLUDEDLICENSE')}
                    disabled={isSubmittingReportJob}
                    onChange={handleCheckFieldChange}
                    />
                </Col>
              )}
            </FormGroup>
            <FormGroup>
              <Col componentClass={ControlLabel} sm={2}>
                Email
              </Col>
              <Col sm={10}>
                <FormControl
                  type="email"
                  placeholder="Email"
                  inputRef={(el: HTMLInputElement) => {
                    emailInputRef.current = el;
                  }}
                  defaultValue={defaultEmail}
                />
                <HelpBlock>The report will be sent to this address upon completion</HelpBlock>
              </Col>
            </FormGroup>
            <br />
            <div className="text-center">
              <Button bsStyle="link" onClick={() => setIsCreatingReport(false)}>
                Cancel
              </Button>
              <Button
                bsStyle="primary"
                className="margin-left-md"
                onClick={() => {
                  if (emailInputRef.current) {
                    onSubmitReport(null, emailInputRef.current.value);
                  }
                }}
                disabled={isSubmittingReportJob}
              >
                <IconText
                  text="Submit Report Job"
                  icon={icons.plane}
                  alignIcon="right"
                />
              </Button>
            </div>
          </Form>
        </div>
      )
    );
  }

  return (
    <div>
      {reports?.length > 0 ? (
        <div>
          <Flex justify="space-between" align="center" gap="8px">
            <h3>
              Report History
            </h3>
            <Flex justify="flex-end" align="center" gap="8px">
              <IconButton id="refresh-reports-button" icon={icons.reload} onClick={onLoad} />
              <Button bsStyle="primary" className="pull-right" onClick={() => setIsCreatingReport(true)}>
                <IconText
                  text="New Report"
                  icon={icons.add}
                />
              </Button>
            </Flex>
          </Flex>
          <ListGroup>
            {reports.map(report => {
              const fileName = report.file;
              const displayName = report.type === 'NOTICE'
                ? `Global Package Report - ${report.time}`
                : `Project Report - ${report.project} - ${report.time}`;
              return (
                <ReportListItem
                  key={fileName}
                  fileName={fileName}
                  displayName={displayName}
                  onSendLink={onSubmitReport}
                  isSubmittingReportJob={isSubmittingReportJob}
                />
              );
            })}
          </ListGroup>
        </div>
      ) : (
        <div className="text-center">
          <br /><br />
          <h3>No Historical Reports</h3>
          <p>It looks like you haven&apos;t created any reports of this kind yet.</p>
          <Button bsStyle="primary" onClick={() => setIsCreatingReport(true)}>
            <IconText
              text="Start New Report"
              icon={icons.add}
            />
          </Button>
        </div>
      )}
    </div>
  );
};

const RELEASE_GROUP_OPTIONS_PARAMS = { page: 1, pageSize: 100, sort: 'asc', skipPagination: true } as const;

const getDefaultConfig = (): Record<string, string[]> => ({
  columns: ['LIBRARY', 'AUTHORS', 'LICENSE', 'OTHERLICENSES'],
});

const ModalBulkReport: FC<Props> = ({
  show,
  showBinaryScansOption,
  onHide,
  selectedTeams,
  onAddJobToNotificationQueue,
}) => {
  const [state, setState] = useState<State>({
    loading: false,
    reports: [],
    error: null,
    config: getDefaultConfig(),
    isCreatingReport: false,
    isSubmittingReportJob: false,
    reportTask: null,
    includeBinaryScans: true,
    releaseGroup: null,
  });

  const addToast = useAddToast();
  const isTeamGroupPackageReportEnabled = useIsFeatureEnabled(TEAM_GROUP_PACKAGE_REPORT);
  // The V1 report has no release group scope, so those orgs keep the unscoped form.
  const isOrgBomV1 = useIsFeatureEnabled(DISABLE_ORG_BOM_V2);
  const scopedTeamIds = isTeamGroupPackageReportEnabled && !isOrgBomV1 && Array.isArray(selectedTeams) ? selectedTeams : [];
  const selectionKey = scopedTeamIds.join(',');
  // The modal stays mounted on the Reports page, so only fetch the options once the creation form is open.
  const { releaseGroups, isLoading: isLoadingReleaseGroups } = useTeamsReleaseGroups(
    scopedTeamIds,
    RELEASE_GROUP_OPTIONS_PARAMS,
    !!show && state.isCreatingReport,
  );
  const releaseGroupOptions: Array<SelectOption<number>> = releaseGroups.map((releaseGroup) => ({
    label: releaseGroup.title,
    value: releaseGroup.id,
  }));
  // The pick only counts while it's still offered under the selection it was made in, so the form never submits a
  // release group it isn't showing (e.g. one removed from the team while the modal was open).
  const pickedReleaseGroupId = state.releaseGroup?.selectionKey === selectionKey ? state.releaseGroup.id : null;
  const releaseGroupId = releaseGroupOptions.some((option) => option.value === pickedReleaseGroupId)
    ? pickedReleaseGroupId
    : null;

  const onLoad = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      loading: true,
      reports: [],
      reportTask: null,
      isCreatingReport: false,
      config: getDefaultConfig(),
      releaseGroup: null,
    }));

    /* eslint-disable promise/always-return */
    apiGet<AttributionFileMetadata[]>('/api/organization/all_attribution_filenames')
      .then(({ data }) => {
        const normalizedReports = _.compact((data || []).map((report) => {
          // Old timestamps were epoch time. New timestamps are in the custom format. We can see which type this is by
          // checking for a '-'
          const formattedTime = (report.time as string).includes('-')
            ? moment(report.time, 'YYYY-MM-DD-HH-mmssSSSZ')
            : moment(new Date(Number(report.time)));

          if (!formattedTime.isValid() || !['PROJECT', 'NOTICE'].includes(report.type)) {
            return undefined;
          }

          return Object.assign(report, {
            timeObj: formattedTime,
            time: formattedTime.format('MMMM Do YYYY, h:mm a'),
          });
        }));

        const sortedReports = _.sortBy(normalizedReports, (report) =>
          // report.timeObj should always exist, so the -1 should never be hit.
          report.timeObj ? -report.timeObj.valueOf() : -1,
        );

        setState(prevState => ({
          ...prevState,
          reports: sortedReports.map((report) => _.omit(report, 'timeObj')),
        }));
      })
      .catch((error) => {
        setState(prevState => ({ ...prevState, error }));
      })
      .finally(() => setState(prevState => ({ ...prevState, loading: false })));
    /* eslint-enable promise/always-return */
  }, []);

  // Equivalent to componentWillReceiveProps
  useEffect(() => {
    if (show) {
      onLoad();
    }
  }, [onLoad, show]);

  const onSubmitReport = async (filename: string | null, email: string) => {
    setState(prevState => ({ ...prevState, isSubmittingReportJob: true }));

    // 'all', undefined, or an empty array should select all teams, otherwise use the selected team(s)
    let teamId: TeamSelection | undefined | 'all' = selectedTeams;
    if (
      _.isNil(selectedTeams)
      || (Array.isArray(selectedTeams) && _.isEmpty(selectedTeams))
    ) {
      teamId = 'all';
    }

    const data = {
      filename,
      email,
      config: {
        ...state.config,
        teamId,
        includeBinaryScans: state.includeBinaryScans,
        ...(releaseGroupId !== null ? { releaseGroupId } : {}),
      },
    };
    try {
      const response = await post<typeof data, Task>('/api/organization/attribution', data);
      const task = response.data;

      //  Add the job to the notification queue, so that it will be polled for completion.
      //  A toast will be shown to the user when the job is complete.
      if (task?.jobToken) {
        onAddJobToNotificationQueue(task.jobToken, {
          success: { content: 'Global Package Report Bundle report emailed.' },
          failure: { content: 'Global Package Report Bundle report failed.' },
        });
      }

      setState(prevState => ({ ...prevState, reportTask: task || null }));
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'Failed to submit the Global Package Report Bundle report.'),
      });
    } finally {
      setState(prevState => ({ ...prevState, isSubmittingReportJob: false }));
    }
  };

  const handleCheckFieldChange = (checkKey: string) => {
    setState(prevState => {
      const newConfig = { ...prevState.config };
      const colIndex = newConfig?.columns?.indexOf(checkKey) ?? -1;
      if (colIndex > -1) {
        newConfig.columns?.splice(colIndex, 1);
      } else {
        newConfig.columns?.push(checkKey);
      }
      return { ...prevState, config: newConfig };
    });
  };

  const handleBinaryScanChange = () => {
    setState(prevState => ({
      ...prevState,
      includeBinaryScans: !prevState.includeBinaryScans,
    }));
  };

  const setIsCreatingReport = (value: boolean) => {
    setState(prevState => ({ ...prevState, isCreatingReport: value }));
  };

  return (
    <Modal
      className="modal-bulk-report modal-horizontal"
      bsSize="large"
      show={show}
      backdrop
      animation
      onHide={onHide}
    >
      <div className="row">
        <div className="col-md-4 modal-horizontal-header">
          {state.isCreatingReport ? (
            <div className="padded">
              <button
                type="button"
                className="clickable btn btn-link p-0 border-0"
                onClick={() => setIsCreatingReport(false)}
              >
                &laquo; Back to Historical Reports
              </button>
              <br />
              <br />
              <h4>Create new report</h4>
              <br />
              <p>Configure what data you want FOSSA to collect about your packages.</p>
              <p>
                Once you submit the request, FOSSA may take up to <b>10 minutes</b> to complete your report (depending
                on the size of your organization&apos;s data).
              </p>
            </div>
          ) : (
            <div className="padded">
              <span className="icon-feature">
                <Icon icon={icons.addToProject} />
              </span>
              <Modal.Title>{window?.user?.organization?.title} Package Report</Modal.Title>
              <br />
              <p>
                This report will collect and export data about every 3rd party packages used across your organization
                and email you the results when finished.
              </p>
              <br />
              <Button bsStyle="primary" onClick={() => setIsCreatingReport(true)}>
                <IconText
                  text="Start New Report"
                  icon={icons.add}
                />
              </Button>
            </div>
          )}
        </div>
        <div className="col-md-8 modal-horizontal-content">
          <div className="padded">
            <ContentPanel
              loading={state.loading}
              reports={state.reports}
              isCreatingReport={state.isCreatingReport}
              reportTask={state.reportTask}
              isSubmittingReportJob={state.isSubmittingReportJob}
              config={state.config}
              includeBinaryScans={state.includeBinaryScans}
              showBinaryScansOption={showBinaryScansOption}
              scopedTeamIds={scopedTeamIds}
              releaseGroupOptions={releaseGroupOptions}
              isLoadingReleaseGroups={isLoadingReleaseGroups}
              releaseGroupId={releaseGroupId}
              onReleaseGroupChange={(id) => setState(prevState => ({
                ...prevState,
                releaseGroup: id === null ? null : { id, selectionKey },
              }))}
              defaultEmail={window?.user?.email ?? ''}
              onLoad={onLoad}
              onSubmitReport={onSubmitReport}
              handleCheckFieldChange={handleCheckFieldChange}
              handleBinaryScanChange={handleBinaryScanChange}
              onHide={onHide}
              setIsCreatingReport={setIsCreatingReport}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

type ReportListItemProps = {
  fileName: string;
  displayName: string;
  onSendLink: (filename: string, email: string) => void;
  isSubmittingReportJob: boolean;
};

export const ReportListItem: FC<ReportListItemProps> = ({
  fileName,
  displayName,
  onSendLink,
  isSubmittingReportJob,
}) => {
  const [isSendingLink, setIsSendingLink] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <ListGroupItem>
      <span className="pull-right">
        {isSendingLink ? (
          <span>
            <input
              type="email"
              defaultValue={window?.user?.email ?? ''}
              ref={emailInputRef}
            />&nbsp;
            <Button onClick={() => setIsSendingLink(false)} bsSize="xsmall">
              x
            </Button>&nbsp;
            <Button
              onClick={() => {
                if (emailInputRef.current) {
                  onSendLink(fileName, emailInputRef.current.value);
                  setIsSendingLink(false);
                }
              }}
              bsSize="xsmall"
              bsStyle="primary"
              disabled={isSubmittingReportJob}
            >
              <Icon icon={icons.plane} />
            </Button>
          </span>
        ) : (
          <Button
            onClick={() => setIsSendingLink(true)}
            bsSize="xsmall"
            disabled={isSubmittingReportJob}
          >
            <IconText
              icon={icons.plane}
              text="Send New Link"
              alignIcon="right"
            />
          </Button>
        )}
      </span>
      <b>{displayName}</b>
    </ListGroupItem>
  );
};

export type ModalBulkReportProps = Omit<Props, 'onAddJobToNotificationQueue'>;

/**
 * HOC/inject the onAddJobToNotificationQueue function into the ModalBulkReport component.
 */
const ModalBulkReportWithAddJob: FC<
  ModalBulkReportProps
> = (props) => {
  const onAddJobToNotificationQueue = useAddJobToNotificationQueue();
  const selectedTeams = useSelector(TeamsModule.selectors.getTeamSelection);

  return (
    <ModalBulkReport
      {...props}
      onAddJobToNotificationQueue={onAddJobToNotificationQueue}
      selectedTeams={selectedTeams}
    />
  );
};

export default ModalBulkReportWithAddJob;
